/**
 * Device filtering.
 *
 * "Does this fit my phone?" is the question this catalogue is actually shopped with, and
 * the storefront cannot answer it: only `filter.v.availability` and `filter.v.price` are
 * active on this store, and filtering by the `custom.compatible_devices` metafield would
 * need the Search & Discovery app. So the answer is computed here, over the grid the
 * server already rendered.
 *
 * That is only sound while the whole collection is on the page, which is why
 * `sections/collection.liquid` refuses to render the chooser when the collection
 * paginates. Nothing in this file has to re-check that.
 *
 * Products are sorted rather than culled. A shopper who picks a model still sees the
 * eleven universal accessories, under their own heading, below the ones made for that
 * model — hiding two thirds of the catalogue to answer a compatibility question would
 * cost more than it explains.
 *
 * The choice is remembered (`hubcart:device` + `:name` in localStorage) so it survives
 * filter re-renders and return visits. A `<device-fit>` element on the product page used
 * to read the same record and answer for the one product being looked at; it was removed
 * on 2026-09-06 — with the chooser off the customer-facing collection page since
 * 2026-09-01, the verdict could only ever quote a stale choice back at a visitor who no
 * longer has any way to change it. If the chooser is re-enabled, that half belongs back
 * with it: see git history.
 */

(function () {
  'use strict';

  window.Hubcart = window.Hubcart || {};
  if (window.Hubcart.deviceFilter) return;

  const STORAGE_KEY = 'hubcart:device';

  /* localStorage throws rather than no-ops in Safari's private mode and wherever site
     data is blocked, and a remembered convenience is never worth taking the page down
     with it. Every access goes through these two. */
  function read() {
    try {
      return window.localStorage.getItem(STORAGE_KEY) || '';
    } catch {
      return '';
    }
  }

  function write(value) {
    try {
      if (value) window.localStorage.setItem(STORAGE_KEY, value);
      else window.localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* Not remembering the choice is the whole cost. */
    }
  }

  /* Both halves of the feature read the same record, so the name and the handle travel
     together: a product page has no chooser to look the name up in. */
  function readName() {
    try {
      return window.localStorage.getItem(`${STORAGE_KEY}:name`) || '';
    } catch {
      return '';
    }
  }

  function writeName(value) {
    try {
      if (value) window.localStorage.setItem(`${STORAGE_KEY}:name`, value);
      else window.localStorage.removeItem(`${STORAGE_KEY}:name`);
    } catch {
      /* As above. */
    }
  }

  function fill(template, values) {
    return Object.keys(values).reduce(
      (text, key) => text.split(`[[${key}]]`).join(values[key]),
      template || ''
    );
  }

  class DeviceFilter extends HTMLElement {
    connectedCallback() {
      /* The section is re-rendered wholesale when a chip removes a Shopify filter
         (`component-facets.js` `navigate()`), so this element can be constructed more
         than once per visit. Binding is idempotent by construction — a fresh element
         carries no listeners — but the restore below has to run every time, or a
         remembered model would be silently forgotten by an unrelated filter change. */
      this.addEventListener('change', (event) => {
        if (!event.target.matches('[data-device-input]')) return;
        write(event.target.value);
        writeName(event.target.dataset.deviceName || '');
        this.apply();
      });

      this.restore();
      this.apply();

      /* Filtering and sorting replace `[data-results]` without touching this element, so
         the fresh grid arrives unsorted and with both group headings hidden. */
      this.onSectionsLoad = () => this.apply();
      document.addEventListener('hubcart:sections:load', this.onSectionsLoad);
    }

    disconnectedCallback() {
      document.removeEventListener('hubcart:sections:load', this.onSectionsLoad);
    }

    /* Ticks the remembered model, if this collection still offers it. A shopper who chose
       a model on a collection that covers it and then moved to one that does not keeps the
       record — the chooser here simply has nothing to tick, and falls back to "All". */
    restore() {
      const stored = read();
      if (!stored) return;

      const input = this.querySelector(`[data-device-input][value="${CSS.escape(stored)}"]`);
      if (!input) return;

      input.checked = true;

      /* The row is wider than a phone and the remembered model is usually not in the first
         screenful of it — an iPhone 13 sits about a thousand pixels along. Left alone, a
         returning shopper sees a grid sorted for a phone whose chip is off the right edge,
         which reads as the grid having reordered itself. Scrolling the rail rather than
         calling scrollIntoView, so the page itself never moves. */
      const rail = input.closest('.device-filter__rail');
      const chip = input.nextElementSibling;
      if (!rail || !chip) return;

      rail.scrollLeft +=
        chip.getBoundingClientRect().left -
        rail.getBoundingClientRect().left -
        (rail.clientWidth - chip.offsetWidth) / 2;
    }

    get selected() {
      return this.querySelector('[data-device-input]:checked');
    }

    apply() {
      const section = this.closest('[data-section-id]') || document;
      const grid = section.querySelector('[data-results] .product-grid');
      if (!grid) return;

      const input = this.selected;
      const handle = input ? input.value : '';
      const name = input ? input.dataset.deviceName || '' : '';
      const items = grid.querySelectorAll('.product-grid__item[data-devices]');

      let matches = 0;

      items.forEach((item) => {
        if (!handle) {
          /* "All devices" is the grid exactly as the server ordered it, so the state is
             removed rather than set to a third value — nothing then overrides the
             merchandiser's sort. */
          delete item.dataset.deviceFit;
          return;
        }

        const devices = item.dataset.devices.split(' ').filter(Boolean);

        if (devices.length === 0) {
          item.dataset.deviceFit = 'universal';
        } else if (devices.includes(handle)) {
          item.dataset.deviceFit = 'match';
          matches += 1;
        } else {
          /* Made for a different phone. Hidden rather than sorted to the end: this is the
             one group that is genuinely no use to this shopper. */
          item.dataset.deviceFit = 'none';
        }
      });

      this.paintHeadings(grid, handle, name, matches);
      this.announce(name, matches);
    }

    /**
     * The two group headings live in the grid as full-width list items rather than as
     * headings wrapped around two separate grids, so the cards stay in one `<ul>` and one
     * set of grid tracks — two grids would let the columns of the second disagree with the
     * first whenever the group sizes differed.
     */
    paintHeadings(grid, handle, name, matches) {
      const match = grid.querySelector('[data-device-group="match"]');
      const universal = grid.querySelector('[data-device-group="universal"]');

      /* Both are emptied rather than merely hidden when there is no model to name. Their
         copy is a sentence about a specific phone, and a sentence with the phone left out
         of it is the kind of thing that survives a CSS failure and reaches a shopper. */
      if (match) {
        match.hidden = !handle || matches === 0;
        match.textContent = handle ? fill(this.dataset.headingMatch, { device: name }) : '';
      }

      if (universal) {
        universal.hidden = !handle;
        if (!handle) {
          universal.textContent = '';
        } else {
          /* Nothing made for this model is a real answer, not an empty state — the eleven
             universal accessories below still fit it. The heading says so rather than
             leaving the shopper to infer it from a group that lost its sibling. */
          universal.textContent =
            matches === 0
              ? fill(this.dataset.headingNone, { device: name })
              : this.dataset.headingUniversal;
        }
      }
    }

    announce(name, matches) {
      const status = this.querySelector('[data-device-status]');
      if (!status) return;

      if (!name) {
        status.textContent = '';
        return;
      }

      let template = this.dataset.statusOther;
      if (matches === 0) template = this.dataset.statusNone;
      else if (matches === 1) template = this.dataset.statusOne;

      status.textContent = fill(template, { device: name, count: matches });
    }
  }

  /* Guarded, like every other element in the theme: the theme editor can load this script
     twice when a section is re-added, and a second define() on a taken name throws. */
  if (!customElements.get('device-filter')) {
    customElements.define('device-filter', DeviceFilter);
  }

  window.Hubcart.deviceFilter = { read, readName };
})();
